import React, { useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Search, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AssignSchoolsModal({ open, onClose, portfolio, headers, onChanged }) {
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const assignedIds = new Set((portfolio?.schools || []).map((s) => s.tenant_id));

  const load = async (q = "") => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/portfolios/tenants/available?q=${encodeURIComponent(q)}`, { headers });
      setTenants(r.data.tenants || []);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { if (open) { setSelected({}); load(""); } }, [open]); // eslint-disable-line

  const toggle = (tid) => setSelected((s) => ({ ...s, [tid]: !s[tid] }));

  const save = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) { toast.error("Select at least one school"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/portfolios/${portfolio.id}/schools`, { tenant_ids: ids }, { headers });
      toast.success(`Added ${ids.length} school(s)`);
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add schools");
    } finally {
      setSaving(false);
    }
  };

  const removeSchool = async (tid) => {
    try {
      await axios.delete(`${API}/api/admin/portfolios/${portfolio.id}/schools/${tid}`, { headers });
      toast.success("School removed");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="assign-schools-modal" className="max-w-2xl">
        <DialogHeader><DialogTitle>Assign Schools — {portfolio?.name}</DialogTitle></DialogHeader>

        {!!(portfolio?.schools || []).length && (
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="mb-2 text-xs font-medium uppercase text-slate-500">Currently assigned ({portfolio.schools.length})</div>
            <div className="flex flex-wrap gap-2">
              {portfolio.schools.map((s) => (
                <span key={s.tenant_id} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700">
                  {s.school_name}
                  <button data-testid={`school-remove-btn-${s.tenant_id}`} onClick={() => removeSchool(s.tenant_id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input data-testid="assign-schools-search" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(search)} placeholder="Search schools…" className="pl-8" />
        </div>

        <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
          {loading ? (
            <div className="flex h-32 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : (
            <div className="divide-y divide-slate-100">
              {tenants.map((t) => {
                const already = assignedIds.has(t.tenant_id);
                return (
                  <label key={t.tenant_id} data-testid={`assign-school-row-${t.tenant_id}`} className={`flex items-center gap-3 px-3 py-2 text-sm ${already ? "opacity-50" : "hover:bg-slate-50 cursor-pointer"}`}>
                    <Checkbox checked={already || !!selected[t.tenant_id]} disabled={already} onCheckedChange={() => toggle(t.tenant_id)} />
                    <div className="flex-1">
                      <div className="font-medium text-slate-800">{t.name}</div>
                      <div className="text-xs text-slate-400">{t.contact_email || t.tenant_id}{t.has_diagnostic ? " · has diagnostic" : ""}</div>
                    </div>
                    {already && <span className="text-xs text-slate-400">Assigned</span>}
                  </label>
                );
              })}
              {!tenants.length && <div className="p-6 text-center text-sm text-slate-400">No schools found.</div>}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button data-testid="assign-schools-save" onClick={save} disabled={saving}>{saving ? "Adding…" : "Add Selected"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
